import moment from 'moment'
import { groupBy } from 'lodash'
import {
  calculateValuesForCsv,
  dateDiff,
  filterBySearch,
  isBlank,
  newTableSortExt,
  sumTrackerRows,
  IInsightsV1FavoriteStatistics,
} from '../../shared'
import {
  availableGroupBy,
  dateDiffFormats,
  dateDiffUnits,
  defaultGroupBy,
  groupFilterKeys,
  omitCsvColumns,
} from './constants'

const allowedBlankFilterKeys = ['creatorNotIds']

export const normalizeGroupBy = (
  value: string,
  datePickerState?: any
): string => {
  if (value === null) {
    return normalizeGroupBy(window.location.hash.substr(1) || '')
  } else if (
    value === 'date' &&
    moment(datePickerState.range.startDate).format('YYYY-MM-DD') ===
      moment(datePickerState.range.endDate).format('YYYY-MM-DD')
  ) {
    return 'hour'
  } else if (value === 'boost') {
    return 'name'
  } else if (availableGroupBy.includes(value)) {
    return value
  } else {
    return defaultGroupBy
  }
}

export const normalizeFilter = (opts: {
  datePickerState: any
  filters: any
  group?: string
  origin?: string
  trafficFilter?: boolean
  clickFilter?: boolean
  activeSince?: boolean
  withNetworks?: boolean
  withTrackers?: boolean
  usePrevDates?: boolean
}) => {
  const {
    datePickerState,
    filters,
    group,
    origin,
    trafficFilter,
    clickFilter,
    activeSince,
    withNetworks,
    withTrackers,
  } = opts
  const usePrevDates = opts.usePrevDates ?? false
  const result: any = {
    fromDate: moment
      .utc(
        usePrevDates
          ? datePickerState.prevRange.startDate
          : datePickerState.range.startDate
      )
      .add(1, 'day')
      .startOf('day'),
    toDate: moment
      .utc(
        usePrevDates
          ? datePickerState.prevRange.endDate
          : datePickerState.range.endDate
      )
      .endOf('day'),
    filters: {},
  }

  for (const filter of filters) {
    const key = groupFilterKeys[filter.key] || filter.key
    if (filter.value || allowedBlankFilterKeys.includes(key)) {
      switch (filter.key) {
        case 'date':
          {
            const selectedDate = new Date(filter.value)
            result.fromDate = moment.utc(selectedDate).startOf('day')
            result.toDate = moment.utc(selectedDate).endOf('day')
          }
          break
        case 'month':
          {
            const selectedMonth = new Date(filter.value)
            result.fromDate = moment.utc(selectedMonth).startOf('month')
            result.toDate = moment.utc(selectedMonth).endOf('month')
          }
          break
        case 'year':
          {
            const selectedYear = new Date(filter.value)
            result.fromDate = moment.utc(selectedYear).startOf('year')
            result.toDate = moment.utc(selectedYear).endOf('year')
          }
          break
        case 'tags':
          {
            const [tagKey, tagValue] = filter.value.split(' = ')
            if (tagKey && tagValue) {
              result.filters.tags = {
                [tagKey]: tagValue,
              }
            }
          }
          break
        case 'networkUuids':
          {
            if (filter.value === 'Unknown') {
              result.filters[key] = ''
            } else {
              result.filters[key] = filter.value.split(', ')
              const allIndex = result.filters[key].indexOf('all')
              if (allIndex !== -1) result.filters[key].splice(allIndex, 1)
            }
          }
          break
        case 'assigneeUserUuids':
        case 'advertiserStatuses':
          break
        default:
          {
            if (filter.value === 'Unknown') {
              result.filters[key] = ''
            } else {
              result.filters[key] = filter.value.split(', ')
            }
          }
          break
      }
    }
  }

  if (group === 'boost') {
    if (!result.filters.boostDepartment?.length) {
      result.filters.boostDepartment = ['all']
    }
  } else {
    if (result.filters.boostDepartment) {
      delete result.filters.boostDepartment
    }
  }

  if (origin) {
    result.filters.origin = origin
  }

  if (trafficFilter) {
    result.filters.withPartialTraffic = true
  }

  if (clickFilter) {
    result.filters.minClicks = 50
  }

  if (activeSince) {
    result.filters.withActiveSince = true
  }

  // hlt-backend skips the per-row uuid list when its flag is false; absent means on
  if (withNetworks !== undefined) {
    result.filters.withNetworks = withNetworks
  }
  if (withTrackers !== undefined) {
    result.filters.withTrackers = withTrackers
  }

  return result
}

export const groupStatisticsRows = (opts: {
  data: any
  prevData?: any
  group: string
  datePickerState: any
}) => {
  const { data, prevData, group, datePickerState } = opts

  if (!data?.rows?.length) return []

  if (group === 'tracker') {
    // Group rows by matching name
    const rowsByName = groupBy(data.rows, (row) => row.name)
    const prevRowsByName = prevData
      ? groupBy(prevData.rows, (row) => row.name)
      : {}
    return Object.keys(rowsByName).map((name) => {
      return {
        ...sumTrackerRows(rowsByName[name]),
        prev: prevRowsByName[name]?.length
          ? sumTrackerRows(prevRowsByName[name])
          : undefined,
      }
    })
  } else if (prevData) {
    return data.rows.map((row) => {
      let adjustGrp = (value) => value
      if (['date', 'hour', 'week', 'month', 'year'].includes(group)) {
        const sameDate =
          moment(datePickerState.range.startDate).format('YYYY-MM-DD') ===
          moment(datePickerState.range.endDate).format('YYYY-MM-DD')
        const key = group === 'date' && sameDate ? 'hour' : group
        const t = dateDiffUnits[key]
        const f = dateDiffFormats[key]
        const diff = dateDiff(
          datePickerState.range.startDate,
          datePickerState.range.endDate,
          t
        )
        adjustGrp = (grp) => moment(grp, f).add(diff, t).add(1, t).format(f)
      }
      const prev = prevData.rows.find(
        (prevRow) => adjustGrp(prevRow.grp) === row.grp
      )
      return {
        ...row,
        prev,
      }
    })
  } else {
    return data.rows
  }
}

export const enrichStatisticsRows = (opts: {
  rows: any
  group: string
  adsStats: any
}) => {
  const { rows, group, adsStats } = opts

  if (group !== 'name' || !adsStats) return rows

  for (const row of rows) {
    const domain = row.grp || row.name
    const stat = adsStats.find((stat) => stat.domain && stat.domain === domain)
    row['googleAdsCost'] = stat?.cost ?? 0
    row['googleAdsRoas'] = stat?.cost
      ? (row.conversionTotalCommission || 0) / stat.cost
      : 0
  }

  return rows
}

export const statisticsSearch = (collection: any, search: string) => {
  const excludeUnknownRow = collection?.filter(
    (row) => (row.grp || row.name) && row.name.toLowerCase() !== 'unknown'
  )
  return filterBySearch(excludeUnknownRow, search, (row: any) => [
    row.grp,
    row.name,
    'Unknown',
  ])
}

export const statisticsCustomFilter = (opts: {
  data: any
  prevData?: any
  search: string
  filters: any
  favorites: any
  assignees: any
  statuses: any
  group: string
  crFilter?: boolean
  ecrFilter?: boolean
  favFilter?: boolean
}) => {
  const {
    data: collection,
    prevData: prevCollection,
    search,
    group,
    filters,
    assignees,
    statuses,
    crFilter,
    ecrFilter,
    favFilter,
  } = opts
  const favorites = (opts.favorites || []).filter(
    (favorite) => favorite.favoriteGroupBy === group
  )

  let result = collection

  if (group === 'name') {
    const assigneeFilter = filters?.find(
      (filter) => filter.key === 'assigneeUserUuids' && filter.value
    )
    if (assigneeFilter) {
      const assigneeUserUuids = assigneeFilter.value
        .split(',')
        .map((uuid) => uuid.trim())
        .filter(Boolean)
      result = result.filter((row) => {
        const domain = row.grp || row.name
        return (
          assignees.find(
            (assignee) =>
              assignee.assigneeAdvertiserDomain === domain &&
              assigneeUserUuids.includes(assignee.assigneeUserUuid)
          ) ||
          (assigneeUserUuids.includes('none') &&
            !assignees.find(
              (assignee) => assignee.assigneeAdvertiserDomain === domain
            ))
        )
      })
    }

    const statusFilter = filters?.find(
      (filter) => filter.key === 'advertiserStatuses'
    )
    if (statusFilter) {
      const pairs = statusFilter.value
        .split(',')
        .map((value) => value.trim().split(':'))
      result = result.filter((row) => {
        const domain = row.grp || row.name
        if (statusFilter.value === '') {
          return !statuses.find(
            (advertiserStatus) =>
              advertiserStatus.advertiserStatusAdvertiserDomain === domain
          )
        } else {
          return !!statuses.find(
            (advertiserStatus) =>
              advertiserStatus.advertiserStatusAdvertiserDomain === domain &&
              pairs.find(
                (pair) =>
                  pair[0] === advertiserStatus.advertiserStatusType &&
                  pair[1] === advertiserStatus.advertiserStatusValue
              )
          )
        }
      })
    }
  }

  if (crFilter || ecrFilter || favFilter) {
    result = result.filter((row) => {
      let crMatch = false
      let ecrMatch = false
      let favMatch = false
      if (crFilter) {
        crMatch =
          (row.grp || row.name) &&
          row.name.toLowerCase() !== 'unknown' &&
          row.clickCount &&
          (row.conversionCount / row.clickCount) * 100 <= 0.8
      }
      if (ecrFilter) {
        const prev =
          row.prev ||
          prevCollection?.find(
            (prevRow) =>
              (row.grp && row.grp == prevRow.grp) ||
              (row.name && row.name == prevRow.name)
          )
        if (prev && prev.clickCount >= 500) {
          const ecr =
            (row.conversionTotalCommission / (row.conversionSale || 1)) * 100
          const prevEcr =
            (prev.conversionTotalCommission / (prev.conversionSale || 1)) * 100
          const ecrDiff = (ecr / prevEcr) * 100
          ecrMatch = ecrDiff <= 85
        }
      }
      if (favFilter) {
        favMatch = !!favorites.find(
          (favorite) =>
            favorite.favoriteGroupName === row.grp ||
            favorite.favoriteGroupName === row.name
        )
      }
      return crMatch || ecrMatch || favMatch
    })
  }

  if (search) {
    return statisticsSearch(result, search)
  } else {
    return result
  }
}

export const statisticsSort = newTableSortExt(
  (a: any, b: any, key: string, dir: 'asc' | 'desc') => {
    if (key === 'name') {
      return [
        a.name.toLowerCase() || a.grp.toLowerCase() || 'unknown',
        b.name.toLowerCase() || b.grp.toLowerCase() || 'unknown',
      ]
    }
    if (key === 'ctr') {
      return [
        a.pageviewCount > 0 ? a.clickCount / a.pageviewCount : 0,
        b.pageviewCount > 0 ? b.clickCount / b.pageviewCount : 0,
      ]
    }
    if (key === 'cr') {
      return [
        a.clickCount > 0 ? a.conversionCount / a.clickCount : 0,
        b.clickCount > 0 ? b.conversionCount / b.clickCount : 0,
      ]
    }
    if (key === 'rr') {
      const totalA = a.conversionCount + a.rejectedConversionCount
      const totalB = b.conversionCount + b.rejectedConversionCount
      return [
        totalA ? a.rejectedConversionCount / totalA : 0,
        totalB ? b.rejectedConversionCount / totalB : 0,
      ]
    }
    if (key === 'rcr') {
      const totalA =
        a.conversionTotalCommission + a.rejectedConversionCommission
      const totalB =
        b.conversionTotalCommission + b.rejectedConversionCommission
      return [
        totalA ? a.rejectedConversionCommission / totalA : 0,
        totalB ? b.rejectedConversionCommission / totalB : 0,
      ]
    }
    if (key === 'epc') {
      return [
        a.clickCount > 0 ? a.conversionTotalCommission / a.clickCount : 0,
        b.clickCount > 0 ? b.conversionTotalCommission / b.clickCount : 0,
      ]
    }
    if (key === 'epm') {
      return [
        a.pageviewCount > 0
          ? a.conversionTotalCommission / (a.pageviewCount / 1000)
          : 0,
        b.pageviewCount > 0
          ? b.conversionTotalCommission / (b.pageviewCount / 1000)
          : 0,
      ]
    }
    if (key === 'ecr') {
      return [
        a.conversionSale > 0
          ? (a.conversionTotalCommission / a.conversionSale) * 100
          : 0,
        b.conversionSale > 0
          ? (b.conversionTotalCommission / b.conversionSale) * 100
          : 0,
      ]
    }
    if (key === 'rejectedCount') {
      return [a.rejectedConversionCount, b.rejectedConversionCount]
    }
    if (key === 'grossSale') {
      return [
        a.conversionSale + a.conversionRejectedSale,
        b.conversionSale + b.conversionRejectedSale,
      ]
    }
    if (key === 'grossConversionCount') {
      return [
        a.conversionCount + a.rejectedConversionCount,
        b.conversionCount + b.rejectedConversionCount,
      ]
    }
    if (key === 'conversionClickCommission') {
      return [a.cpcCommission, b.cpcCommission]
    }
    if (key === 'googleAdsCost') {
      return [a.googleAdsCost, b.googleAdsCost]
    }
  }
)

export const statisticsPaginate = (collection: any, limit: number) => {
  return collection.slice(0, limit)
}

export const findFavorite = (
  favorites: any,
  group: string,
  name1: string = '',
  name2: string = ''
) => {
  return favorites?.find(
    (favorite: IInsightsV1FavoriteStatistics) =>
      favorite.favoriteGroupBy === group &&
      ((name1 && favorite.favoriteGroupName === name1) ||
        (name2 && favorite.favoriteGroupName === name2))
  )
}

export const formatFilterDates = (datePickerState) => {
  const result = {}
  const date = moment(datePickerState.range.endDate)

  while (date.isAfter(datePickerState.range.startDate)) {
    const value = date.format('YYYY-MM-DD')
    result[value] = value
    date.subtract(1, 'day')
  }

  return result
}

export const sumRows = (rows: any) => {
  const result = {
    conversionCount: 0,
    rejectedConversionCount: 0,
    conversionCommission: 0,
    rejectedConversionCommission: 0,
    cpcCommission: 0,
    rejectedCpcCommission: 0,
    conversionClickCommission: 0,
    conversionTotalCommission: 0,
    conversionPendingCommission: 0,
    conversionRejectedCommission: 0,
    conversionBoostAddedValue: 0,
    conversionUnboostedCommission: 0,
    conversionSale: 0,
    conversionRejectedSale: 0,
    clickCount: 0,
    pageviewCount: 0,
    prev: undefined,
  }
  if (isBlank(rows)) return result
  for (const row of rows) {
    for (const column in row) {
      result[column] = result[column] || 0
      if (typeof row[column] === 'number') {
        result[column] += row[column]
      }
      if (row.prev && row.prev[column]) {
        result.prev = result.prev || {}
        result.prev[column] = result.prev[column] || 0
        if (typeof row.prev[column] === 'number') {
          result.prev[column] += row.prev[column]
        }
      }
    }
  }
  return result
}

export const filterCsvColumns = (
  columns: string[],
  availableColumns: string[]
) => {
  const csvColumns = columns.filter((c) => availableColumns.includes(c))
  if (csvColumns.includes('advertiserStatus')) {
    csvColumns.splice(
      csvColumns.indexOf('advertiserStatus'),
      1,
      'advertiserStatusPartner',
      'advertiserStatusDeal'
    )
  }
  return csvColumns
}

export const formatCsvFilename = (opts: {
  group: string
  datePickerState: any
}): string => {
  const { group, datePickerState } = opts

  const parts: string[] = ['outbound', '_', group, '_']
  if (datePickerState.prevRangeEnabled) {
    parts.push(moment(datePickerState.range.startDate).format('YYMMDD'))
    parts.push('-')
    parts.push(moment(datePickerState.range.endDate).format('YYMMDD'))
    parts.push('_')
    parts.push(moment(datePickerState.prevRange.startDate).format('YYMMDD'))
    parts.push('-')
    parts.push(moment(datePickerState.prevRange.endDate).format('YYMMDD'))
  } else {
    parts.push(moment(datePickerState.range.startDate).format('YYMMDD'))
    parts.push('-')
    parts.push(moment(datePickerState.range.endDate).format('YYMMDD'))
  }
  parts.push('.csv')
  return parts.join('')
}

export const newCsvRowFormatter =
  (opts: {
    group: string
    columns: string[]
    networks: any[]
    assignees: any[]
    statuses: any[]
    adsStats: any[]
    currencyCode: string
  }) =>
  (row: any) => {
    const out = calculateValuesForCsv(row, opts.currencyCode)

    if (opts.columns.includes('advertiserStatusPartner')) {
      const partnerStatus = opts.statuses?.find(
        (advertiserStatus) =>
          advertiserStatus.advertiserStatusAdvertiserDomain === row.grp &&
          advertiserStatus.advertiserStatusType === 'partner'
      )
      out['advertiserStatusPartner'] = partnerStatus?.advertiserStatusValue
    }

    if (opts.columns.includes('advertiserStatusDeal')) {
      const dealStatus = opts.statuses?.find(
        (advertiserStatus) =>
          advertiserStatus.advertiserStatusAdvertiserDomain === row.grp &&
          advertiserStatus.advertiserStatusType === 'deal'
      )
      out['advertiserStatusDeal'] = dealStatus?.advertiserStatusValue
    }

    if (opts.columns.includes('networks')) {
      const shownNetworks = opts.networks?.filter((network) =>
        (row.clickNetworkUuids || row.conversionNetworkUuids || '')
          .split(',')
          .includes(network.networkUuid)
      )
      out['networks'] = shownNetworks
        .map((network) => network.networkName)
        .filter((v, i, a) => a.indexOf(v) === i)
        .join(', ')
    }

    if (opts.columns.includes('trackers')) {
      const count = (row.clickTrackerUuids || '').split(',').length
      out['trackers'] = `${count} campaign${count === 1 ? '' : 's'}`
    }

    if (opts.columns.includes('assignees')) {
      const shownAssignees =
        opts.assignees?.filter(
          (assignee) => assignee.assigneeAdvertiserDomain === row.grp
        ) || []
      out['assignees'] = shownAssignees
        .map(
          (assignee) =>
            `${assignee.assigneeUserFirstname} ${assignee.assigneeUserLastname[0]}.`
        )
        .join(', ')
    }

    if (opts.group === 'name') {
      const domain = row.grp || row.name
      const stat = opts.adsStats?.find(
        (stat) => stat.domain && stat.domain === domain
      )
      if (opts.columns.includes('googleAdsCost')) {
        out['googleAdsCost'] = stat?.cost?.toFixed(2)
      }
      if (opts.columns.includes('googleAdsRoas')) {
        out['googleAdsRoas'] = stat?.cost
          ? ((row.conversionTotalCommission || 0) / stat.cost).toFixed(2)
          : undefined
      }
    }

    for (const column in out) {
      if (omitCsvColumns.includes(column)) continue
      if (out.prev?.[column]) {
        out[column] = `${out[column]}\n${out.prev[column]}`
      }
    }

    return out
  }
